import React, { createContext, useContext, useState } from 'react';
import {
	useAutomationDiscoverySpotlight,
	type Column,
} from '../use-automation-discovery-spotlight';
import { useAutomationRulesBadge } from '../use-automation-rules-badge';

export const useAutomationDiscovery = (columns: Column[]) => {
	const [isAutomationDiscoveryModalOpen, setIsAutomationDiscoveryModalOpen] =
		useState<boolean>(false);
	const [statusName, setStatusName] = useState<string>('');

	const {
		targetColumnId,
		shouldShowSpotlight,
		dismissSpotlight,
		setSpotlightTargetHasRendered,
		spotlightTargetHasRendered,
		isTargetColumn,
	} = useAutomationDiscoverySpotlight(columns);

	const onSetAutomationModalOpen = (isOpen: boolean, status?: string) => {
		setIsAutomationDiscoveryModalOpen(isOpen);
		if (status) {
			setStatusName(status);
		}
	};

	const { automationRulesCount } = useAutomationRulesBadge();

	return {
		isAutomationDiscoveryModalOpen,
		setIsAutomationDiscoveryModalOpen: onSetAutomationModalOpen,
		shouldShowSpotlight,
		targetColumnId,
		dismissSpotlight,
		statusName,
		setSpotlightTargetHasRendered,
		spotlightTargetHasRendered,
		isTargetColumn,
		automationRulesCount,
	};
};

export const AutomationDiscoveryContext = createContext<ReturnType<typeof useAutomationDiscovery>>({
	isAutomationDiscoveryModalOpen: false,
	// Replace with lodash/noop
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	setIsAutomationDiscoveryModalOpen: (_isOpen: boolean) => {},
	shouldShowSpotlight: (_columnId: number) => false,
	targetColumnId: '',
	// Replace with lodash/noop
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	dismissSpotlight: () => {},
	statusName: '',
	// Replace with lodash/noop
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	setSpotlightTargetHasRendered: () => {},
	spotlightTargetHasRendered: false,
	isTargetColumn: (_columnId: number) => false,
	automationRulesCount: 0,
});

export const useAutomationDiscoveryContext = () => useContext(AutomationDiscoveryContext);

export const AutomationDiscoveryProvider = ({
	children,
	columns,
}: {
	children: React.ReactNode;
	columns: Column[];
}) => {
	const {
		isAutomationDiscoveryModalOpen,
		setIsAutomationDiscoveryModalOpen,
		shouldShowSpotlight,
		targetColumnId,
		dismissSpotlight,
		statusName,
		setSpotlightTargetHasRendered,
		spotlightTargetHasRendered,
		isTargetColumn,
		automationRulesCount,
	} = useAutomationDiscovery(columns);

	return (
		<AutomationDiscoveryContext.Provider
			value={{
				isAutomationDiscoveryModalOpen,
				setIsAutomationDiscoveryModalOpen,
				shouldShowSpotlight,
				targetColumnId,
				isTargetColumn,
				setSpotlightTargetHasRendered,
				spotlightTargetHasRendered,
				dismissSpotlight,
				statusName,
				automationRulesCount,
			}}
		>
			{children}
		</AutomationDiscoveryContext.Provider>
	);
};
